import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import api, { formatDate } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Shield, Mail, MessageCircle, Activity, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, Eye, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

const SUPER_ADMIN_EMAIL = 'ahmad.disi@unknownroom.com';

export default function SuperAdminAuditPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check access
  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();

  useEffect(() => {
    if (isSuperAdmin) {
      loadSummary();
    }
  }, [isSuperAdmin]);

  const loadSummary = async () => {
    try {
      const { data } = await api.get('/admin/audit/summary');
      setSummary(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="w-96 bg-card border-border">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">{t('audit.accessDenied')}</h2>
            <p className="text-muted-foreground">{t('audit.superAdminOnly')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="audit-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-3xl text-foreground tracking-tight flex items-center gap-3">
            <Shield className="w-8 h-8 text-red-500" />
            {t('audit.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{t('audit.subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadSummary}>
          <RefreshCw className="w-4 h-4 me-2" />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('audit.emailLogs')}</p>
                  <p className="text-2xl font-bold text-foreground">{summary.email.total}</p>
                  <p className="text-xs">
                    <span className="text-green-500">{summary.email.sent} sent</span>
                    {' / '}
                    <span className="text-red-500">{summary.email.failed} failed</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('audit.chatThreads')}</p>
                  <p className="text-2xl font-bold text-foreground">{summary.chat.threads}</p>
                  <p className="text-xs text-muted-foreground">
                    {summary.chat.messages} messages
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('audit.activity')}</p>
                  <p className="text-2xl font-bold text-foreground">{summary.activity.work_orders}</p>
                  <p className="text-xs text-muted-foreground">
                    {summary.activity.work_requests} WRs / {summary.activity.imports} imports
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="email" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            {t('audit.emailLogs')}
          </TabsTrigger>
          <TabsTrigger value="chat" className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            {t('audit.chatThreads')}
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            {t('audit.systemActivity')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email">
          <EmailLogsTab />
        </TabsContent>

        <TabsContent value="chat">
          <ChatThreadsTab />
        </TabsContent>

        <TabsContent value="activity">
          <SystemActivityTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Email Logs Tab
function EmailLogsTab() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [eventFilter, setEventFilter] = useState('__all__');

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== '__all__') params.append('status', statusFilter);
      if (eventFilter && eventFilter !== '__all__') params.append('event_type', eventFilter);
      const { data } = await api.get(`/admin/audit/email-logs?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, [statusFilter, eventFilter]);

  const StatusBadge = ({ status }) => {
    const styles = {
      sent: 'bg-green-500/10 text-green-500 border-green-500/20',
      failed: 'bg-red-500/10 text-red-500 border-red-500/20',
      skipped: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      skipped_not_configured: 'bg-amber-500/10 text-amber-500 border-amber-500/20'
    };
    const label = status === 'skipped_not_configured' ? 'Not Configured' : status;
    return <Badge className={styles[status] || ''}>{label}</Badge>;
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{t('audit.emailLogs')} ({total})</CardTitle>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
                <SelectItem value="skipped_not_configured">Not Configured</SelectItem>
              </SelectContent>
            </Select>
            <Select value={eventFilter} onValueChange={setEventFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="wr_created">WR Created</SelectItem>
                <SelectItem value="wo_status_update">WO Status Update</SelectItem>
                <SelectItem value="manager_decision">Manager Decision</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.time')}</th>
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.event')}</th>
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.subject')}</th>
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.recipients')}</th>
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className="border-b border-border/50">
                  <td className="px-4 py-2.5 text-muted-foreground">{formatDate(log.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline">{log.event_type}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-foreground max-w-xs truncate">{log.subject}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{log.recipients?.length || 0}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={log.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// Chat Threads Tab
function ChatThreadsTab() {
  const { t } = useTranslation();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState(null);
  const [typeFilter, setTypeFilter] = useState('__all__');
  const [includeDeleted, setIncludeDeleted] = useState(true);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter && typeFilter !== '__all__') params.append('type_filter', typeFilter);
      params.append('include_deleted', includeDeleted);
      const { data } = await api.get(`/admin/audit/chats/threads?${params}`);
      setThreads(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadThreads(); }, [typeFilter, includeDeleted]);

  const viewThread = async (threadId) => {
    try {
      const { data } = await api.get(`/admin/audit/chats/threads/${threadId}`);
      setSelectedThread(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <>
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t('audit.chatThreads')} ({threads.length})</CardTitle>
            <div className="flex gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="group">Group</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant={includeDeleted ? "default" : "outline"} 
                size="sm"
                onClick={() => setIncludeDeleted(!includeDeleted)}
              >
                {includeDeleted ? 'Including Deleted' : 'Active Only'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.title')}</th>
                  <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.type')}</th>
                  <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.createdBy')}</th>
                  <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.members')}</th>
                  <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.messages')}</th>
                  <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('common.status')}</th>
                  <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase"></th>
                </tr>
              </thead>
              <tbody>
                {threads.map(thread => (
                  <tr key={thread.id} className={`border-b border-border/50 ${thread.is_deleted ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-2.5 font-medium text-foreground">{thread.title}</td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline">{thread.type}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{thread.created_by_name}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{thread.member_count}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{thread.message_count}</td>
                    <td className="px-4 py-2.5">
                      {thread.is_deleted ? (
                        <Badge className="bg-red-500/10 text-red-500">Deleted</Badge>
                      ) : (
                        <Badge className="bg-green-500/10 text-green-500">Active</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <Button variant="ghost" size="sm" onClick={() => viewThread(thread.id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Thread Detail Dialog */}
      <Dialog open={!!selectedThread} onOpenChange={() => setSelectedThread(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedThread?.title}</DialogTitle>
          </DialogHeader>
          {selectedThread && (
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="bg-muted/30 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">Created by: {selectedThread.created_by_name}</p>
                <p className="text-xs text-muted-foreground">Members: {selectedThread.members?.length || 0}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-2">Members</p>
                <div className="space-y-1">
                  {selectedThread.members?.map(m => (
                    <div key={m.id} className="text-sm flex justify-between items-center p-2 bg-muted/20 rounded">
                      <span>{m.user_name} ({m.user_role})</span>
                      <Badge variant="outline" className={m.is_active ? '' : 'opacity-50'}>
                        {m.role_in_chat}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Messages ({selectedThread.messages?.length || 0})</p>
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {selectedThread.messages?.map(msg => (
                      <div key={msg.id} className="p-2 bg-muted/20 rounded">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium">{msg.sender_name}</span>
                          <span className="text-xs text-muted-foreground">{formatDate(msg.created_at)}</span>
                        </div>
                        {msg.message_type === 'image' ? (
                          <img src={msg.image_url} alt="" className="max-h-32 rounded" />
                        ) : (
                          <p className="text-sm">{msg.text}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// System Activity Tab
function SystemActivityTab() {
  const { t } = useTranslation();
  const [wos, setWos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivity();
  }, []);

  const loadActivity = async () => {
    try {
      const { data } = await api.get('/admin/audit/activity/work-orders?limit=50');
      setWos(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t('audit.recentWorkOrders')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">ID</th>
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.branch')}</th>
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.title')}</th>
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('common.status')}</th>
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.assignedTo')}</th>
                <th className="text-start px-4 py-2.5 text-xs font-semibold uppercase">{t('audit.updated')}</th>
              </tr>
            </thead>
            <tbody>
              {wos.map(wo => (
                <tr key={wo.id} className="border-b border-border/50">
                  <td className="px-4 py-2.5 font-mono text-xs">{wo.id.slice(0, 8)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{wo.branch_name}</td>
                  <td className="px-4 py-2.5 text-foreground">{wo.work_request_title || wo.id.slice(0, 8)}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline">{wo.status}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{wo.assigned_to_name || '-'}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{formatDate(wo.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
